import { type Infer, v } from 'convex/values'

// Product-catalog enums, shared shapes and caps — the single source of truth
// for the global `catalog*` tables in `schema.ts`, the ingest contract in
// `catalogImport.ts` and every function validator. Mirrors the
// `productEnums.ts` pattern. The literal arrays are exported alongside the
// validators so the Zod contract can build its enums from the SAME lists
// instead of a second hand-written copy.

/**
 * The closed top-level taxonomy. Coarse on purpose — it is a filter, not a
 * classification system; the long tail lives in the free-text `subcategory`.
 * `other` is the escape hatch, never a default the tools should reach for.
 *
 * The keys are English because they are IDENTIFIERS, and every identifier in
 * this codebase is English — including the ones a reader meets in a URL, which
 * is where these surface (`?cat=lighting`). The Portuguese a Brazilian
 * designer actually reads is the LABEL, and it lives behind a Lingui message
 * in `web/src/lib/catalog-labels.ts`. Keeping the two apart is what lets the
 * catalog be shown in either language without renaming a single stored row.
 */
export const CATALOG_CATEGORIES = [
  'furniture',
  'lighting',
  'surfaces',
  'fixtures_and_fittings',
  'appliances',
  'decor',
  'doors_and_windows',
  'construction',
  'office',
  'kids',
  'plants',
  'commercial',
  'other',
] as const
export const catalogCategory = v.union(...CATALOG_CATEGORIES.map((c) => v.literal(c)))
export type CatalogCategory = (typeof CATALOG_CATEGORIES)[number]

/**
 * Product lifecycle. Nothing in the catalog is ever deleted: `discontinued`
 * is what a brand said (or a tool proved), `hidden` is the takedown lever —
 * an editorial or rights decision that removes the row from every read
 * while keeping its observations. Absence from a later crawl NEVER moves a
 * row here; only an explicit status row does.
 */
export const CATALOG_PRODUCT_STATUSES = ['active', 'discontinued', 'hidden'] as const
export const catalogProductStatus = v.union(...CATALOG_PRODUCT_STATUSES.map((s) => v.literal(s)))
export type CatalogProductStatus = (typeof CATALOG_PRODUCT_STATUSES)[number]

/** Brand lifecycle. `hidden` hides the brand and, by extension, its rows. */
export const CATALOG_BRAND_STATUSES = ['active', 'hidden'] as const
export const catalogBrandStatus = v.union(...CATALOG_BRAND_STATUSES.map((s) => v.literal(s)))
export type CatalogBrandStatus = (typeof CATALOG_BRAND_STATUSES)[number]

/** Downloadable files a product page links to (blocks, sheets, catalogs). */
export const CATALOG_ASSET_KINDS = [
  'sketchup',
  'revit',
  'archicad',
  'autocad',
  'ies',
  'technical_sheet',
  'pdf_catalog',
] as const
export const catalogAssetKind = v.union(...CATALOG_ASSET_KINDS.map((k) => v.literal(k)))
export type CatalogAssetKind = (typeof CATALOG_ASSET_KINDS)[number]

/**
 * Who published the page an observation came from. `brand` is the maker's
 * own site, `retailer` a store, `aggregator` an index such as Casoca,
 * `manual` a staff edit in the admin — the rank ladder in `catalogRank.ts`
 * is built on this and on `market`.
 */
export const CATALOG_SOURCE_ROLES = ['brand', 'retailer', 'aggregator', 'manual'] as const
export const catalogSourceRole = v.union(...CATALOG_SOURCE_ROLES.map((r) => v.literal(r)))
export type CatalogSourceRole = (typeof CATALOG_SOURCE_ROLES)[number]

/** The roles a scraping tool may claim on the wire — `manual` is the admin's alone. */
export const CATALOG_IMPORT_SOURCE_ROLES = ['brand', 'retailer', 'aggregator'] as const
export const catalogImportSourceRole = v.union(
  ...CATALOG_IMPORT_SOURCE_ROLES.map((r) => v.literal(r))
)
export type CatalogImportSourceRole = (typeof CATALOG_IMPORT_SOURCE_ROLES)[number]

/**
 * Which market a source speaks for. Facts and imagery are welcome from any
 * market; commercial truth (reference price, the default configuration,
 * availability) only ever comes from `br`. `global` is a brand's
 * international site (natuzzi.com), `other` anything not worth its own bucket.
 */
export const CATALOG_MARKETS = ['br', 'global', 'us', 'eu', 'other'] as const
export const catalogMarket = v.union(...CATALOG_MARKETS.map((m) => v.literal(m)))
export type CatalogMarket = (typeof CATALOG_MARKETS)[number]

/**
 * What an image shows. `swatch` is a finish/fabric sample bound to an option
 * value through `selection`; `drawing` a technical line drawing.
 */
export const CATALOG_IMAGE_KINDS = ['main', 'detail', 'ambient', 'drawing', 'swatch'] as const
export const catalogImageKind = v.union(...CATALOG_IMAGE_KINDS.map((k) => v.literal(k)))
export type CatalogImageKind = (typeof CATALOG_IMAGE_KINDS)[number]

/**
 * The nature of an option axis. `size` and `module` values may carry their
 * own dimensions; `finish` covers fabrics, leathers, colours and coatings.
 */
export const CATALOG_OPTION_KINDS = ['size', 'finish', 'module', 'other'] as const
export const catalogOptionKind = v.union(...CATALOG_OPTION_KINDS.map((k) => v.literal(k)))
export type CatalogOptionKind = (typeof CATALOG_OPTION_KINDS)[number]

/**
 * How sure the tool was that an observation belongs to the `productKey` it
 * named. `exact` means a hard join (the brand page links to the aggregator
 * page, or the code matched); `fuzzy` means a heuristic that a human should
 * confirm in the review queue before the row is trusted.
 */
export const CATALOG_MATCH_CONFIDENCES = ['exact', 'fuzzy'] as const
export const catalogMatchConfidence = v.union(...CATALOG_MATCH_CONFIDENCES.map((c) => v.literal(c)))
export type CatalogMatchConfidence = (typeof CATALOG_MATCH_CONFIDENCES)[number]

/**
 * `catalogProducts.matchSource`: the confidence of the identity a row rests
 * on. Starts as the creating observation's confidence, drops to `fuzzy` when
 * a fuzzy observation attaches later, and becomes `manual` once staff has
 * reviewed the row in the match queue.
 */
export const CATALOG_MATCH_SOURCES = [...CATALOG_MATCH_CONFIDENCES, 'manual'] as const
export const catalogMatchSource = v.union(...CATALOG_MATCH_SOURCES.map((c) => v.literal(c)))
export type CatalogMatchSource = (typeof CATALOG_MATCH_SOURCES)[number]

/**
 * How much the projection knows about a product. Derived from `completeness`
 * on every rebuild; drives the enrichment queue and the admin filters.
 */
export const CATALOG_ENRICHMENTS = ['thin', 'partial', 'rich'] as const
export const catalogEnrichment = v.union(...CATALOG_ENRICHMENTS.map((e) => v.literal(e)))
export type CatalogEnrichment = (typeof CATALOG_ENRICHMENTS)[number]

/** Outcome of one `POST /catalog/ingest` request — all-or-nothing per batch. */
export const CATALOG_IMPORT_BATCH_STATUSES = ['accepted', 'rejected'] as const
export const catalogImportBatchStatus = v.union(
  ...CATALOG_IMPORT_BATCH_STATUSES.map((s) => v.literal(s))
)
export type CatalogImportBatchStatus = (typeof CATALOG_IMPORT_BATCH_STATUSES)[number]

// ── Caps ────────────────────────────────────────────────────────────────────
// Enforced by the Zod contract on the way in and by the schema's shape on the
// way down; both read these constants, never their own numbers.

/** Product / brand display name. Same ceiling as the acervo's `products.name`. */
export const CATALOG_NAME_MAX = 200
/** Brand SKU / reference code. */
export const CATALOG_CODE_MAX = 80
/** URL-safe slug on brands and products. */
export const CATALOG_SLUG_MAX = 120
/** Normalised subcategory key (`sofa_modular`, `pendente`). */
export const CATALOG_SUBCATEGORY_MAX = 80
/** Plain-text description. */
export const CATALOG_DESCRIPTION_MAX = 5000
/** Designer and collection labels. */
export const CATALOG_LABEL_MAX = 120
/** Every URL the catalog stores — same parse ceiling as `products.storeUrl`. */
export const CATALOG_URL_MAX = 2048
/** Free-text `productKey` a tool mints for one product identity. */
export const CATALOG_PRODUCT_KEY_MAX = 200
/** Entries in `materials[]` / `colors[]`, and the length of each. */
export const CATALOG_TAG_LIST_MAX = 20
export const CATALOG_TAG_MAX = 80
/** Long-tail `attributes` record: entry count, key length, value length. */
export const CATALOG_ATTRIBUTES_MAX = 60
export const CATALOG_ATTRIBUTE_KEY_MAX = 60
export const CATALOG_ATTRIBUTE_VALUE_MAX = 500
/** Option axes per product and values per axis. */
export const CATALOG_OPTIONS_MAX = 6
export const CATALOG_OPTION_VALUES_MAX = 80
/** Real, priced/measured combinations per product (never the full matrix). */
export const CATALOG_VARIANTS_MAX = 200
/**
 * Images per product across every source.
 *
 * 50 rather than 40 because the real corpus disagreed with the guess: 91 of
 * Casoca's 30,742 products carry more than 40 photos and one carries 94, and
 * a single over-cap row rejects its whole 200-row batch. The ceiling is a
 * budget guard, not an editorial opinion — the average product has 5.4 images,
 * so the worst case moves from 8,000 image rows per batch to 10,000 while the
 * typical batch stays near 1,100. An adapter must still truncate to this
 * rather than hand the contract a payload it defines as invalid.
 */
export const CATALOG_IMAGES_PER_PRODUCT_MAX = 50
/** Downloadable assets per product. */
export const CATALOG_ASSETS_PER_PRODUCT_MAX = 20
/** Domains a brand adapter covers. */
export const CATALOG_SOURCE_DOMAINS_MAX = 10
/** Observations per `POST /catalog/ingest` request. Over this is a 413. */
export const CATALOG_IMPORT_BATCH_MAX = 200
/**
 * Bytes per request body, checked from `content-length` BEFORE the JSON is
 * parsed — the observation cap can only be read after parsing, so without this
 * a caller holding the bearer token could make the action buffer and parse an
 * arbitrarily large document first. The largest measured legitimate batch
 * (200 observations × 40 images) is 3.8 MB, so this leaves ample headroom.
 */
export const CATALOG_IMPORT_BODY_BYTES_MAX = 8 * 1024 * 1024
/** Rejection reasons kept on a `catalogImportBatches` row. */
export const CATALOG_IMPORT_REJECTIONS_MAX = 50
/** Prefix every catalog image key must carry in the public bucket. */
export const CATALOG_IMAGE_KEY_PREFIX = 'catalog/'
/** Vector size for `catalogProducts.embedding` (text-embedding-3-small). */
export const CATALOG_EMBEDDING_DIMENSIONS = 1536

// ── Shared shapes ───────────────────────────────────────────────────────────
// The content shapes that appear BOTH on the projection (`catalogProducts`)
// and inside every observation payload, so an observation can carry any
// subset of what a product row holds. The Zod contract mirrors these field
// for field; `catalogImport.ts` pins the two together at compile time.

/**
 * Centimetres. Any subset — a rug has width/length, a pendant a diameter and
 * height. Product-level dimensions describe the reference configuration;
 * per-size dimensions live on the option value.
 */
export const catalogDimensionsCm = v.object({
  depth: v.optional(v.number()),
  diameter: v.optional(v.number()),
  height: v.optional(v.number()),
  length: v.optional(v.number()),
  width: v.optional(v.number()),
})
export type CatalogDimensionsCm = Infer<typeof catalogDimensionsCm>

/**
 * One value on an option axis. `key` is the stable, normalised handle a
 * `selection` refers to (`86-sofa`, `eolo-linen-01`); `label` is what the
 * page showed. `group` clusters swatches ("Velvet", "Woven"). `dimensionsCm`
 * is for size/module values; `priceDeltaCents` for finishes that add cost.
 */
export const catalogOptionValue = v.object({
  dimensionsCm: v.optional(catalogDimensionsCm),
  group: v.optional(v.string()),
  key: v.string(),
  label: v.string(),
  priceDeltaCents: v.optional(v.number()),
})
export type CatalogOptionValue = Infer<typeof catalogOptionValue>

/**
 * An option AXIS ("Tamanho", "Revestimento", "Módulo") with its values —
 * axes instead of a variant per combination, so a sofa with 3 sizes × 40
 * fabrics is 43 values, not 120 rows. `key` is the normalised axis handle
 * every `selection` record and every swatch image points at.
 */
export const catalogOption = v.object({
  key: v.string(),
  kind: catalogOptionKind,
  label: v.string(),
  values: v.array(catalogOptionValue),
})
export type CatalogOption = Infer<typeof catalogOption>

/**
 * The axis as the PROJECTION stores it: the observation shape plus the
 * value `rank()` chose as the reference configuration — computed from BR
 * sources only, so a foreign retailer's default size never becomes ours.
 * Never on the wire; `rebuildProduct` mints it.
 */
export const catalogProjectedOption = v.object({
  ...catalogOption.fields,
  defaultValueKey: v.optional(v.string()),
})
export type CatalogProjectedOption = Infer<typeof catalogProjectedOption>

/**
 * A REAL combination that changes price, code or size — never the cartesian
 * product. `selection` maps axis key → value key. Foreign-market prices stay
 * here with their currency and never reach the product's `refPriceCents`.
 */
export const catalogVariant = v.object({
  code: v.optional(v.string()),
  dimensionsCm: v.optional(catalogDimensionsCm),
  key: v.string(),
  refPriceCents: v.optional(v.number()),
  refPriceCurrency: v.optional(v.string()),
  selection: v.record(v.string(), v.string()),
})
export type CatalogVariant = Infer<typeof catalogVariant>

/**
 * A downloadable file as an observation reports it. `externalUrl` (the link
 * on the source page) is the default; `key` only when the crawler actually
 * mirrored the bytes into the catalog bucket.
 */
export const catalogAssetRef = v.object({
  externalUrl: v.string(),
  key: v.optional(v.string()),
  kind: catalogAssetKind,
  label: v.optional(v.string()),
  // Set together with `key`: the hash of the mirrored bytes. One brand's PDF
  // catalog is typically linked from every one of its products, so without a
  // content key the same file uploads once per product. Mirrors what
  // `catalogImageRef` already does, and doubles as change detection.
  sha256: v.optional(v.string()),
  sizeBytes: v.optional(v.number()),
})
export type CatalogAssetRef = Infer<typeof catalogAssetRef>

/**
 * An image as an observation reports it: already uploaded to the public
 * bucket under `catalog/…` by the tool, deduped by `sha256`. `selection`
 * binds a swatch or a per-size photo to option values.
 */
export const catalogImageRef = v.object({
  alt: v.optional(v.string()),
  height: v.number(),
  key: v.string(),
  kind: catalogImageKind,
  selection: v.optional(v.record(v.string(), v.string())),
  sha256: v.string(),
  sourceUrl: v.string(),
  thumbKey: v.string(),
  width: v.number(),
})
export type CatalogImageRef = Infer<typeof catalogImageRef>

/**
 * A brand collection a page files the product under (Casoca calls these
 * "lançamentos": ADM Coleção Alma, Rudnick ABIMAD 41). `slug` is the tools'
 * key inside the brand; `collection` on the payload stays the display label
 * and is derived from `name` when absent. Upserted into `catalogCollections`
 * by (brand, slug); `firstSeenAt` there is what makes a collection "new".
 */
export const catalogCollectionRef = v.object({
  name: v.string(),
  slug: v.string(),
  sourceUrl: v.optional(v.string()),
  year: v.optional(v.number()),
})
export type CatalogCollectionRef = Infer<typeof catalogCollectionRef>

/**
 * The content an observation may carry — every field optional except the
 * name and category, since a thin aggregator page knows little and a brand
 * page knows a lot. The projection on `catalogProducts` holds the same
 * fields, chosen per field by `rank()`.
 */
export const catalogObservationPayload = v.object({
  assets: v.optional(v.array(catalogAssetRef)),
  attributes: v.optional(v.record(v.string(), v.string())),
  category: catalogCategory,
  code: v.optional(v.string()),
  collection: v.optional(v.string()),
  collectionRef: v.optional(catalogCollectionRef),
  colors: v.optional(v.array(v.string())),
  description: v.optional(v.string()),
  designer: v.optional(v.string()),
  dimensionsCm: v.optional(catalogDimensionsCm),
  materials: v.optional(v.array(v.string())),
  name: v.string(),
  options: v.optional(v.array(catalogOption)),
  refPriceCents: v.optional(v.number()),
  refPriceCurrency: v.optional(v.string()),
  refPriceUrl: v.optional(v.string()),
  subcategory: v.optional(v.string()),
  variants: v.optional(v.array(catalogVariant)),
})
export type CatalogObservationPayload = Infer<typeof catalogObservationPayload>

/**
 * The brand object embedded on every content observation. Upserted by
 * `slug`; the tools own the slug, so two adapters naming the same brand must
 * agree on it.
 */
export const catalogBrandRef = v.object({
  country: v.optional(v.string()),
  logoKey: v.optional(v.string()),
  name: v.string(),
  slug: v.string(),
  sourceDomains: v.array(v.string()),
  websiteUrl: v.string(),
})
export type CatalogBrandRef = Infer<typeof catalogBrandRef>

/**
 * One source's contribution to a projected product, rebuilt from its
 * observations on every ingest: which page, whose, for which market, and
 * the fields it currently wins.
 */
export const catalogSource = v.object({
  domain: v.string(),
  fetchedAt: v.number(),
  fields: v.array(v.string()),
  market: catalogMarket,
  role: catalogSourceRole,
  url: v.string(),
})
export type CatalogSource = Infer<typeof catalogSource>

// ── The wire batch, as the ingest mutation receives it ──────────────────────
// The HTTP action validates the body with the Zod contract in
// `catalogImport.ts` and hands the typed batch to the mutation; these are
// the same shapes as Convex validators so the mutation's args are checked
// too. `catalogImport.ts` pins each pair together at compile time.

export const catalogContentObservationInput = v.object({
  brand: catalogBrandRef,
  contentHash: v.string(),
  fetchedAt: v.number(),
  images: v.array(catalogImageRef),
  kind: v.literal('content'),
  market: catalogMarket,
  matchConfidence: catalogMatchConfidence,
  payload: catalogObservationPayload,
  productKey: v.string(),
  role: catalogImportSourceRole,
  sourceUrl: v.string(),
})
export type CatalogContentObservationInput = Infer<typeof catalogContentObservationInput>

export const catalogStatusObservationInput = v.object({
  kind: v.literal('status'),
  productKey: v.string(),
  status: catalogProductStatus,
})
export type CatalogStatusObservationInput = Infer<typeof catalogStatusObservationInput>

export const catalogImportBatchInput = v.object({
  observations: v.array(v.union(catalogContentObservationInput, catalogStatusObservationInput)),
  schemaVersion: v.number(),
})
export type CatalogImportBatchInput = Infer<typeof catalogImportBatchInput>
